import React from 'react'
import { join, path } from 'ramda'

import { SubmitResponse, Values } from '@ecm/data/Form'
import { HubSpotError, HubSpotResponse, isOnlyInvalidEmailError } from '@ecm/data/HubSpot'
import { Lead } from '@ecm/data/Lead'
import { runClientEff } from '@ecm/effect'
import TermsAndService from '@ecm/ui/component/form/TermsAndService'
import Color from '@ecm/ui/element/Color'
import { validateEmail, validateRequired } from '@ecm/ui/element/Form/Validation'
import FormWrapper from '@ecm/ui/element/Form/Wrapper'
import { Form } from '@ecm/ui/element/input/Form'
import { FormInput } from '@ecm/ui/element/input/FormInput'

export type GenericLeadFormProps = {
  buttonWidth?: string
  border?: boolean
  className?: string
  formId: string
  landingPage?: boolean
  maxWidth?: string
  minWidth?: string
  onFailure: (message: string) => () => void
  onSuccess: (lead: Lead, response: HubSpotResponse) => () => void
  green?: boolean
  source?: string
  submitText?: string
  title?: React.ReactNode
  width?: string
}

const GenericLeadForm = (props: GenericLeadFormProps) => {
  const onSubmit = async (values: Values): Promise<SubmitResponse> => {
    if (!values.email) {
      return {
        message: 'Email is required',
        onComplete: props.onFailure('Email was not provided'),
        status: 'error',
      }
    }
    const lead: Lead = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
    }

    return runClientEff(async eff => {
      const response: HubSpotResponse = await eff.submitGenericLead(
        lead,
        props.formId,
        props.source
      )

      const status: 'success' | 'error' =
        response.errors && !isOnlyInvalidEmailError(response.errors) ? 'error' : 'success'
      const message: string | undefined =
        response.errors && !isOnlyInvalidEmailError(response.errors)
          ? join(', ', path<HubSpotError[]>(['errors'], response) || [])
          : path(['inlineMessage'], response)

      const onSuccessResponse: () => void = props.onSuccess(lead, response)

      return {
        message,
        onComplete: onSuccessResponse,
        status,
      }
    })
  }

  return (
    <FormWrapper
      heading={props.title}
      backgroundColor={Color.white.normal}
      border={props.border}
      width={props.width}
      maxWidth={props.maxWidth}
      minWidth={props.minWidth}
    >
      <Form
        name="exampleForm"
        onSubmit={onSubmit}
        submitText={props.submitText}
        buttonWidth={props.buttonWidth}
        green={props.green}
      >
        <FormInput
          name="firstName"
          id="firstName"
          label="First Name"
          placeHolder="Alex"
          validation={{ rule: validateRequired }}
          width="100%"
        />

        <FormInput
          name="lastName"
          id="lastName"
          label="Last Name"
          placeHolder="Smith"
          validation={{ rule: validateRequired }}
        />

        <FormInput
          id="email"
          name="email"
          label="Email"
          type="email"
          placeHolder="alex.smith@business.com"
          validation={{ rule: validateEmail }}
        />

        <TermsAndService landingPage />
      </Form>
    </FormWrapper>
  )
}

export default GenericLeadForm
